import { devLog } from '../utils/logger';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth, getAuthHeaders } from '../contexts/AuthContext';
import {
  Crown, Star, Zap, CheckCircle, Loader2, ArrowLeft, Shield, TrendingUp, Eye
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const planIcons = { basic: Star, premium: Crown, gold: Zap };
const planColors = {
  basic: { bg: 'bg-[#6B8E7C]', light: 'bg-[#6B8E7C]/10', text: 'text-[#6B8E7C]' },
  premium: { bg: 'bg-[#D4A373]', light: 'bg-[#D4A373]/10', text: 'text-[#D4A373]' },
  gold: { bg: 'bg-[#9B8BAE]', light: 'bg-[#9B8BAE]/10', text: 'text-[#9B8BAE]' }
};

export default function FeaturedPlansPage() {
  const { user } = useAuth();
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);

  useEffect(() => {
    fetchPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPlans = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/featured/plans`);
      setPlans(data.plans);
    } catch (err) {
      devLog.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (planId) => {
    if (!user) return;
    setPurchasing(planId);
    try {
      const { data } = await axios.post(`${API_URL}/api/featured/checkout`, {
        plan_id: planId,
        origin_url: window.location.origin
      }, { headers: getAuthHeaders(), withCredentials: true });

      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao processar');
    } finally {
      setPurchasing(null);
    }
  };

  const isProfessional = user?.role === 'professional';

  if (!isProfessional) {
    return (
      <div className="min-h-screen flex flex-col bg-[#F9F8F6]">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Crown className="w-16 h-16 mx-auto text-[#A0A09A] mb-4" strokeWidth={1} />
            <p className="text-[#63635E] text-lg mb-2">Área exclusiva para profissionais</p>
            <p className="text-[#A0A09A] text-sm mb-4">Planos de destaque estão disponíveis apenas para profissionais cadastrados</p>
            <Link to="/dashboard" className="btn-secondary inline-block">Voltar ao Dashboard</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#F9F8F6]">
      <Header />
      <main className="flex-1 py-12" data-testid="featured-plans-page">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-[#63635E] hover:text-[#292927] mb-8">
            <ArrowLeft className="w-4 h-4" /> Voltar ao Dashboard
          </Link>

          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4A373]/10 text-[#D4A373] text-sm font-medium mb-4">
              <Crown className="w-4 h-4" strokeWidth={1.5} />
              Destaque seu perfil
            </div>
            <h1 className="text-3xl sm:text-4xl font-light text-[#292927] mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Planos de Destaque
            </h1>
            <p className="text-[#63635E] max-w-xl mx-auto">
              Aumente sua visibilidade e atraia mais clientes com nossos planos de destaque
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 text-[#4A5D4E] animate-spin" />
            </div>
          ) : plans ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              {Object.entries(plans).map(([planId, plan], idx) => {
                const Icon = planIcons[planId] || Star;
                const colors = planColors[planId] || planColors.basic;
                const isPremiumPlan = planId === 'premium';
                return (
                  <div
                    key={planId}
                    className={`card-theraflow relative overflow-hidden ${isPremiumPlan ? 'ring-2 ring-[#D4A373]' : ''}`}
                    data-testid={`plan-${planId}`}
                  >
                    {isPremiumPlan && (
                      <div className="absolute top-4 right-4 px-3 py-1 bg-[#D4A373] text-white text-xs font-medium rounded-full">
                        Mais Popular
                      </div>
                    )}
                    <div className="p-6">
                      <div className={`w-14 h-14 rounded-2xl ${colors.bg} flex items-center justify-center mb-5`}>
                        <Icon className="w-7 h-7 text-white" strokeWidth={1.5} />
                      </div>
                      <h3 className="text-xl font-medium text-[#292927] mb-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                        {plan.name}
                      </h3>
                      <div className="flex items-baseline gap-1 mb-5">
                        <span className="text-3xl font-light text-[#292927]">
                          R${plan.amount.toFixed(2).replace('.', ',')}
                        </span>
                        <span className="text-sm text-[#A0A09A]">/mês</span>
                      </div>
                      <ul className="space-y-3 mb-6">
                        {plan.benefits.map((b) => (
                          <li key={b} className="flex items-start gap-2 text-sm text-[#63635E]">
                            <CheckCircle className={`w-4 h-4 ${colors.text} flex-shrink-0 mt-0.5`} strokeWidth={2} />
                            {b}
                          </li>
                        ))}
                      </ul>
                      {isProfessional ? (
                        <button
                          onClick={() => handlePurchase(planId)}
                          disabled={purchasing === planId}
                          className={`w-full py-3 rounded-full font-medium transition-all flex items-center justify-center gap-2 ${
                            isPremiumPlan
                              ? 'bg-[#D4A373] text-white hover:bg-[#C49363]'
                              : 'bg-[#4A5D4E] text-white hover:bg-[#3E4E41]'
                          }`}
                          data-testid={`buy-${planId}`}
                        >
                          {purchasing === planId ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>Contratar</>
                          )}
                        </button>
                      ) : (
                        <Link to="/register/professional" className="w-full py-3 rounded-full font-medium bg-[#EAE8E3] text-[#63635E] flex items-center justify-center">
                          Cadastre-se como profissional
                        </Link>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null}

          {/* Benefits */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-6">
              <Eye className="w-8 h-8 mx-auto text-[#4A5D4E] mb-3" strokeWidth={1.5} />
              <h4 className="font-medium text-[#292927] mb-1">Mais Visibilidade</h4>
              <p className="text-sm text-[#63635E]">Apareça no topo das buscas e recomendações</p>
            </div>
            <div className="text-center p-6">
              <TrendingUp className="w-8 h-8 mx-auto text-[#4A5D4E] mb-3" strokeWidth={1.5} />
              <h4 className="font-medium text-[#292927] mb-1">Mais Clientes</h4>
              <p className="text-sm text-[#63635E]">Profissionais em destaque recebem 3x mais contatos</p>
            </div>
            <div className="text-center p-6">
              <Shield className="w-8 h-8 mx-auto text-[#4A5D4E] mb-3" strokeWidth={1.5} />
              <h4 className="font-medium text-[#292927] mb-1">Selo Exclusivo</h4>
              <p className="text-sm text-[#63635E]">Badge de destaque visível no seu perfil</p>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
